import { inject, Injectable } from '@angular/core';
import {
  type SearchSuggestionItem,
  type SearchSuggestionOpener,
} from '@modules/common/components/search-popup/search-suggestion-opener';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import type { MessageFolderSummaryDto } from '@sharedlib/dtos/dto/mail-app/folder.dto';
import { MailRailFolderTrees } from './mail-rail-folder-trees.service';
import { MailReaderOpenService } from './mail-reader-open.service';

/**
 * Opens an email suggested in the search popup in Mail's reader (the third panel), without running the search.
 *
 * It does not open anything itself: it hands the email to {@link MailReaderOpenService}, the path a click in the list
 * takes (searchopen r2, Pavel 2026-10-29). So the reader's skeleton shows at once with the subject the suggestion
 * carries — the date, which a suggestion lacks, stays a skeleton bar — the email is read under the mailbox it was
 * suggested from (it may be in any folder or mailbox), and the later of an "Open email" and a list click wins.
 * A folder ("Open folder") opens as a click on it in the rail does.
 */
@Injectable()
export class MailSuggestionOpener implements SearchSuggestionOpener {
  public readonly itemTypes = [StitchType.MESSAGE, StitchType.MESSAGE_FOLDER];

  private readonly readerOpen = inject(MailReaderOpenService);
  private readonly railFolders = inject(MailRailFolderTrees);

  public labelFor(itemType: StitchType): string {
    return itemType === StitchType.MESSAGE_FOLDER ? 'Open folder' : 'Open email';
  }

  public open(item: SearchSuggestionItem, config: StitchRequestConfig): void {
    if (!this.itemTypes.includes(item.itemType)) {
      return;
    }
    if (item.itemType === StitchType.MESSAGE_FOLDER) {
      // As a click on that folder in the rail (searchopen r4): the rail tree holding it runs its own open. A folder no
      // mounted tree shows still opens in the reader, so the button never does nothing.
      if (!this.railFolders.open(item.id, item.tenantId)) {
        this.readerOpen.open(
          { id: item.id, itemType: StitchType.MESSAGE_FOLDER, title: item.title } as MessageFolderSummaryDto,
          config,
          'search',
        );
      }
      return;
    }
    const summary = {
      id: item.id,
      itemType: StitchType.MESSAGE,
      title: item.title,
      accountId: item.tenantId,
    } as MessageSummaryDto;
    this.readerOpen.open(summary, config, 'search');
  }
}
